// Reports Batch 5 — Downtime, Equipment, Handover, Daily Ops Summary, LOT/FIT.
// Deterministic template builders + checklists + quick-phrase chips.
// No AI; no auto-grading; LOT/FIT never certifies formation integrity.
import { polishProse, MISSING } from "@/lib/ddr/templates";

const orMissing = (v) => (v && String(v).trim() ? String(v).trim() : MISSING);
const polishedOrMissing = (v) => polishProse(v) || MISSING;

const sec = (lines, label) => lines.push(`\n=== ${label.toUpperCase()} ===\n`);

// =============================================================================
// 1. DOWNTIME REPORT
// =============================================================================
export function buildDowntimeReport(state) {
  const h = state.header || {};
  const e = state.event || {};
  const root = state.root || {};
  const cost = state.cost || {};
  const followUp = state.followUp || {};
  const lines = [];
  const push = (s) => lines.push(s);

  sec(lines, "Report Header");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Tour / Shift: ${orMissing(h.tour)}`);

  sec(lines, "Downtime Event");
  push(`Category (user-entered): ${orMissing(e.category)}`);
  push(`Sub-Category: ${orMissing(e.subCategory)}`);
  push(`Start: ${orMissing(e.startTime)}  ·  End: ${orMissing(e.endTime)}`);
  push(`Duration (user-entered, hours): ${orMissing(e.durationHrs)}`);
  push(`Depth at Event: ${orMissing(e.depth)}`);
  push(`Activity at Time of Event: ${polishedOrMissing(e.activity)}`);

  sec(lines, "Event Description");
  push(polishedOrMissing(e.description));

  sec(lines, "Suspected Cause / Under Review");
  push("Recorded as entered. This tool does not assign root cause, fault, or contractual category.");
  push(polishedOrMissing(root.suspectedCause));
  push(`Cause Category (user-entered, not classified by tool): ${orMissing(root.causeCategory)}`);

  sec(lines, "Corrective Actions");
  push(polishedOrMissing(root.correctiveActions));

  sec(lines, "Equipment / Personnel Involved");
  push(`Equipment: ${polishedOrMissing(root.equipmentInvolved)}`);
  push(`Personnel: ${polishedOrMissing(root.personnelInvolved)}`);

  sec(lines, "Estimated Cost / Impact (User-Entered)");
  push(`Estimated Cost Impact: ${orMissing(cost.estCost)}`);
  push(`Operational Impact Notes: ${polishedOrMissing(cost.impactNotes)}`);
  push("All cost / time values recorded as user-entered. Final reconciliation is outside this tool.");

  sec(lines, "Follow-Up / Recommendations");
  push(polishedOrMissing(followUp.recommendations));
  push(`Company Procedure Reference (if known): ${orMissing(followUp.companyProcedureRef)}`);

  sec(lines, "Notes");
  push(polishedOrMissing(state.notes?.roughNotes));

  sec(lines, "Disclaimer");
  push("Downtime report is a draft record only. This tool does not classify NPT vs operational time, does not assign contractual responsibility, and does not approve cost recovery. Submit under company procedures for review and approval.");

  return lines.join("\n");
}
export const downtimeChecklist = (s) => {
  const out = [];
  const h = s.header || {}, e = s.event || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.preparedBy) out.push("Prepared-by missing");
  if (!e.category) out.push("Event category missing");
  if (!e.startTime) out.push("Event start time missing");
  if (!e.endTime) out.push("Event end time missing");
  if (!e.description) out.push("Event description missing");
  return out;
};
export const DOWNTIME_QUICK_PHRASES = [
  "Operation resumed after corrective action.",
  "No HSE impact observed.",
  "Suspected mechanical issue — under review.",
  "Suspected weather standby — confirm with marine.",
  "Awaiting parts / awaiting service.",
  "Crew rotated; operation continued.",
  "Recorded for client distribution.",
];

// =============================================================================
// 2. EQUIPMENT FAILURE REPORT
// =============================================================================
export function buildEquipmentFailureReport(state) {
  const h = state.header || {};
  const eq = state.equipment || {};
  const fail = state.failure || {};
  const action = state.action || {};
  const result = state.result || {};
  const lines = [];
  const push = (s) => lines.push(s);

  sec(lines, "Report Header");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}  ·  Department: ${orMissing(h.department)}`);

  sec(lines, "Equipment Details");
  push(`Equipment Tag / Name: ${orMissing(eq.tag)}`);
  push(`Manufacturer: ${orMissing(eq.manufacturer)}  ·  Model: ${orMissing(eq.model)}`);
  push(`Serial Number: ${orMissing(eq.serial)}`);
  push(`Location on Rig: ${orMissing(eq.location)}`);
  push(`Service Hours / Cycles (user-entered): ${orMissing(eq.serviceHours)}`);

  sec(lines, "Failure Details");
  push(`Date / Time of Failure: ${orMissing(fail.dateTime)}`);
  push(`Operation In Progress: ${polishedOrMissing(fail.operation)}`);
  push(`Failure Description:`);
  push(polishedOrMissing(fail.description));
  push(`Symptoms / Indications (as observed): ${polishedOrMissing(fail.symptoms)}`);
  push(`Suspected Cause (user-entered, under review): ${polishedOrMissing(fail.suspectedCause)}`);
  push("Root cause analysis is outside this tool. Recorded values are user-entered observations only.");

  sec(lines, "Troubleshooting / Action Taken");
  push(polishedOrMissing(action.steps));
  push(`Parts Replaced: ${polishedOrMissing(action.partsReplaced)}`);
  push(`Tools / Equipment Used: ${polishedOrMissing(action.toolsUsed)}`);
  push(`Personnel Involved: ${polishedOrMissing(action.personnel)}`);

  sec(lines, "Post-Repair Status");
  push(`Status (user-entered, not certified by tool): ${orMissing(result.status)}`);
  push(`Test / Verification Performed (user-entered): ${polishedOrMissing(result.verification)}`);
  push(`Returned to Service: ${orMissing(result.returnedToService)}`);
  push("This tool does not certify equipment fitness-for-service. Inspection / certification must follow OEM and company procedures.");

  sec(lines, "Follow-Up / Recommendations");
  push(polishedOrMissing(state.followUp?.recommendations));

  sec(lines, "Notes");
  push(polishedOrMissing(state.notes?.roughNotes));

  sec(lines, "Disclaimer");
  push("Equipment failure report is a draft record only. Does not certify root cause, does not approve return to service, and does not replace OEM, third-party inspection, or company maintenance procedures.");

  return lines.join("\n");
};
export const equipmentFailureChecklist = (s) => {
  const out = [];
  const h = s.header || {}, eq = s.equipment || {}, f = s.failure || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.preparedBy) out.push("Prepared-by missing");
  if (!eq.tag) out.push("Equipment tag / name missing");
  if (!f.dateTime) out.push("Date / time of failure missing");
  if (!f.description) out.push("Failure description missing");
  return out;
};
export const EQUIPMENT_FAILURE_QUICK_PHRASES = [
  "Symptom observed during normal operation.",
  "Suspected component failure — under review.",
  "Awaiting OEM service advisory.",
  "Spare available on rig.",
  "Spare not available — awaiting logistics.",
  "Component sent for inspection.",
  "Returned to service after function test.",
];

import { getWellStatusSnapshot, formatSnapshotForText } from "./wellStatusSnapshot";

// =============================================================================
// 3. HANDOVER NOTES (Outgoing → Incoming)
// =============================================================================
export function buildHandoverNotes(state) {
  const h = state.header || {};
  const o = state.outgoing || {};
  const inc = state.incoming || {};
  const ops = state.ops || {};
  const concerns = state.concerns || {};
  const lines = [];
  const push = (s) => lines.push(s);

  sec(lines, "Handover Header");
  push(`Date: ${orMissing(h.reportDate)}  ·  Tour: ${orMissing(h.tour)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);

  sec(lines, "Outgoing Personnel");
  push(`Name: ${orMissing(o.name)}  ·  Position: ${orMissing(o.position)}`);

  sec(lines, "Incoming Personnel");
  push(`Name: ${orMissing(inc.name)}  ·  Position: ${orMissing(inc.position)}`);

  // Current Well Status — auto-aggregated read-only snapshot of the latest
  // cross-report data. Surfaced in both the on-screen polished preview AND
  // the exported PDF so the incoming crew sees the same picture both places.
  sec(lines, "Current Well Status (Auto-Aggregated)");
  try {
    const snap = getWellStatusSnapshot();
    const body = formatSnapshotForText(snap);
    push(body || "No saved drafts or calculator sheets available yet.");
    if (snap.warnings && snap.warnings.length > 0) {
      push("");
      push("Watch items detected — verify against rig data:");
      for (const w of snap.warnings) push(`  - ${w.replace(/^⚠️\s*/, "")}`);
    }
    push("");
    push("Source: Daily Drilling Report, Daily Mud Report, Surface/Subsea BOP Test Report,");
    push("Bit Run Record, saved MAASP and Pump Output calculator sheets.");
    push("Verify all values against live rig data before relying on this snapshot.");
  } catch (_e) {
    push("Current well status snapshot unavailable (no saved drafts found yet).");
  }

  sec(lines, "Current Operation");
  push(`Current Activity: ${polishedOrMissing(ops.currentActivity)}`);
  push(`Current Depth / Status: ${orMissing(ops.currentDepth)}`);
  push(`Current Mud Weight (user-entered): ${orMissing(ops.currentMW)}`);
  push(`Last Casing / Shoe / BHA: ${polishedOrMissing(ops.lastCasing)}`);

  sec(lines, "Last 12-24 Hour Summary");
  push(polishedOrMissing(ops.recentSummary));

  sec(lines, "Planned Activities — Next 12-24 Hours");
  push(polishedOrMissing(ops.plannedActivities));

  sec(lines, "Open Concerns / Watch Items");
  push(polishedOrMissing(concerns.openItems));

  sec(lines, "Equipment Status / Watch Items");
  push(polishedOrMissing(concerns.equipmentWatch));

  sec(lines, "Personnel On Board / Crew Notes");
  push(polishedOrMissing(concerns.pobNotes));

  sec(lines, "Outstanding Tasks / Pending Work Orders");
  push(polishedOrMissing(concerns.outstandingTasks));

  sec(lines, "Communications / Client Updates");
  push(polishedOrMissing(concerns.clientComms));

  sec(lines, "Safety Alerts / Recent Incidents");
  push(polishedOrMissing(concerns.safetyAlerts));

  sec(lines, "Notes");
  push(polishedOrMissing(state.notes?.roughNotes));

  sec(lines, "Sign-Off Placeholder");
  push(`Outgoing: ${orMissing(o.name)}  ·  Incoming: ${orMissing(inc.name)}`);
  push("Signatures recorded outside this tool.");

  sec(lines, "Disclaimer");
  push("Handover notes are a draft summary only. They do not replace verbal handover, walk-around, or company-required handover documentation. Review and confirm under company procedures.");

  return lines.join("\n");
}
export const handoverChecklist = (s) => {
  const out = [];
  const h = s.header || {}, o = s.outgoing || {}, inc = s.incoming || {}, ops = s.ops || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.tour) out.push("Tour / shift missing");
  if (!o.name) out.push("Outgoing name missing");
  if (!inc.name) out.push("Incoming name missing");
  if (!ops.currentActivity) out.push("Current activity missing");
  if (!ops.plannedActivities) out.push("Planned activities missing");
  return out;
};
export const HANDOVER_QUICK_PHRASES = [
  "No incidents during outgoing tour.",
  "Operation continuing as planned.",
  "Watch trip-tank for losses.",
  "Watch pit volume trends.",
  "Awaiting client approval before next step.",
  "Maintenance window scheduled — confirm timing.",
  "Crew change scheduled — confirm POB.",
];

// =============================================================================
// 4. DAILY OPERATIONS SUMMARY (Lightweight one-page)
// =============================================================================
export function buildDailyOperationsSummary(state) {
  const h = state.header || {};
  const s = state.summary || {};
  const next = state.next || {};
  const lines = [];
  const push = (x) => lines.push(x);

  sec(lines, "Daily Operations Summary");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Day on Well: ${orMissing(h.dayOnWell)}  ·  Spud Date: ${orMissing(h.spudDate)}`);

  sec(lines, "Current Status");
  push(`Current Depth (MD): ${orMissing(s.currentMd)}  ·  TVD: ${orMissing(s.currentTvd)}`);
  push(`Hole Section: ${orMissing(s.holeSection)}  ·  Last Casing / Shoe: ${orMissing(s.lastCasing)}`);
  push(`Current Mud Weight (ppg, user-entered): ${orMissing(s.currentMW)}`);

  sec(lines, "Operations Summary (Past 24 Hours)");
  push(polishedOrMissing(s.summary24));

  sec(lines, "NPT / Downtime (User-Entered Hours)");
  push(`NPT Hours: ${orMissing(s.nptHours)}  ·  Downtime Hours: ${orMissing(s.downtimeHours)}`);
  push(`NPT Notes: ${polishedOrMissing(s.nptNotes)}`);

  sec(lines, "HSE Snapshot");
  push(polishedOrMissing(s.hseSnapshot));

  sec(lines, "Next 24-Hour Plan");
  push(polishedOrMissing(next.plan));

  sec(lines, "Forecast / Look-Ahead");
  push(polishedOrMissing(next.lookAhead));

  sec(lines, "Notes");
  push(polishedOrMissing(state.notes?.roughNotes));

  sec(lines, "Disclaimer");
  push("Daily Operations Summary is a draft one-page record. It does not replace the Daily Drilling Report, the morning report, or any contractually required reporting. Review under company and client procedures before distribution.");

  return lines.join("\n");
}
export const dailyOpsChecklist = (st) => {
  const out = [];
  const h = st.header || {}, s = st.summary || {}, n = st.next || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!s.currentMd) out.push("Current MD missing");
  if (!s.summary24) out.push("24-hour summary missing");
  if (!n.plan) out.push("Next 24-hour plan missing");
  return out;
};
export const DAILY_OPS_QUICK_PHRASES = [
  "Operations on plan.",
  "No HSE incidents reported.",
  "NPT under review.",
  "Awaiting client decision.",
  "Continuing per drilling program.",
  "Weather standby — monitor sea state.",
];

// =============================================================================
// 5. LOT / FIT REPORT — LEAK-OFF TEST / FORMATION INTEGRITY TEST
//    Strict: record-support only. Never certifies integrity, never declares
//    pass/fail, never says "shoe passed", "integrity confirmed", or
//    "safe to drill ahead". Calculations are clearly labelled as
//    calculation support only.
// =============================================================================
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Calculation helpers — explicitly labelled as user-aid calculations only.
export function lotFitCalcs({ mudWeightPpg, tvdFt, maxSurfPsi }) {
  const mw = num(mudWeightPpg);
  const tvd = num(tvdFt);
  const surf = num(maxSurfPsi);
  if (mw == null || tvd == null || surf == null) return null;
  // Hydrostatic of mud at shoe (psi)
  const hydroPsi = mw * 0.052 * tvd;
  const totalPsi = hydroPsi + surf;
  // Equivalent mud weight (ppg) — calculation support only
  const emwPpg = tvd > 0 ? totalPsi / (0.052 * tvd) : null;
  // Gradient (psi/ft)
  const gradPsiPerFt = tvd > 0 ? totalPsi / tvd : null;
  return {
    hydroPsi: Math.round(hydroPsi * 10) / 10,
    totalPsi: Math.round(totalPsi * 10) / 10,
    emwPpg: emwPpg == null ? null : Math.round(emwPpg * 1000) / 1000,
    gradPsiPerFt: gradPsiPerFt == null ? null : Math.round(gradPsiPerFt * 10000) / 10000,
  };
}

export function buildLotFitReport(state) {
  const h = state.header || {};
  const setup = state.setup || {};
  const data = state.data || {};
  const obs = state.obs || {};
  const lines = [];
  const push = (s) => lines.push(s);

  sec(lines, "Report Header");
  push(`Date: ${orMissing(h.reportDate)}`);
  push(`Rig: ${orMissing(h.rigName)}  ·  Well: ${orMissing(h.wellName)}`);
  push(`Operator / Client: ${orMissing(h.operator)}`);
  push(`Prepared By: ${orMissing(h.preparedBy)}`);
  push(`Test Type (user-entered): ${orMissing(h.testType)}`);
  push("Test type recorded as entered (LOT / FIT / XLOT). This tool does not redefine the test name.");

  sec(lines, "Test Setup (User-Entered)");
  push(`Casing Shoe Depth TVD (ft): ${orMissing(setup.shoeTvd)}`);
  push(`Casing Shoe Depth MD (ft): ${orMissing(setup.shoeMd)}`);
  push(`Hole Size Below Shoe: ${orMissing(setup.holeSize)}`);
  push(`Mud Weight at Test (ppg): ${orMissing(setup.mudWeightPpg)}`);
  push(`Mud Type: ${orMissing(setup.mudType)}`);
  push(`Cement Top / Last Cement: ${orMissing(setup.cementTop)}`);

  sec(lines, "Test Procedure (As Recorded)");
  push(`Pump Rate (user-entered): ${orMissing(data.pumpRate)}`);
  push(`Pressure-Up Increments: ${polishedOrMissing(data.increments)}`);
  push(`Max Surface Pressure Reached (psi, user-entered): ${orMissing(data.maxSurfPsi)}`);
  push(`Leak-Off / Departure Pressure (psi, user-entered, if observed): ${orMissing(data.leakOffPsi)}`);
  push(`Volume Pumped (user-entered): ${orMissing(data.volumePumped)}`);
  push(`Hold Period: ${orMissing(data.holdPeriod)}`);
  push(`Bleed-Off Behaviour (as observed): ${polishedOrMissing(data.bleedOff)}`);

  sec(lines, "Calculation Support (User-Aid Only — Not Certification)");
  const calc = lotFitCalcs({
    mudWeightPpg: setup.mudWeightPpg,
    tvdFt: setup.shoeTvd,
    maxSurfPsi: data.maxSurfPsi,
  });
  if (!calc) {
    push("Calculation support not computed — mud weight, shoe TVD, or max surface pressure missing.");
  } else {
    push(`Hydrostatic at Shoe (calculated, psi): ${calc.hydroPsi}`);
    push(`Total Pressure at Shoe (calculated, psi): ${calc.totalPsi}`);
    push(`Equivalent Mud Weight at Shoe (calculated, ppg): ${calc.emwPpg ?? "Not computed"}`);
    push(`Effective Gradient at Shoe (calculated, psi/ft): ${calc.gradPsiPerFt ?? "Not computed"}`);
  }
  push("");
  push("These values are deterministic mathematical aids only. They do not certify formation integrity, do not declare pass/fail, and must not be used as the sole basis for casing-shoe acceptance. Final interpretation requires competent drilling / well engineering review.");

  sec(lines, "Observations");
  push(polishedOrMissing(obs.observations));

  sec(lines, "Witnesses / Personnel Present");
  push(`Operator / Client Rep: ${orMissing(obs.clientRep)}`);
  push(`Driller / Toolpusher: ${orMissing(obs.driller)}`);
  push(`Mud Engineer: ${orMissing(obs.mudEng)}`);
  push(`Other: ${polishedOrMissing(obs.otherWitnesses)}`);

  sec(lines, "Recorded Status (As Entered by User)");
  push(`User-Entered Status: ${orMissing(obs.userStatus)}`);
  push("Tool does not classify the result. The above is the user's own recorded wording, retained verbatim.");

  sec(lines, "Recommendations / Next Steps");
  push(polishedOrMissing(state.recommendations?.text));

  sec(lines, "Notes");
  push(polishedOrMissing(state.notes?.roughNotes));

  sec(lines, "LOT / FIT Special Warning");
  push("Draft record-support only. This tool does NOT certify formation integrity. It does NOT independently classify the test outcome as a pass or a fail. It does NOT issue acceptance of the casing shoe. It does NOT confirm the integrity status of the formation. It does NOT clear the well for any further operations. All test interpretation must be performed by competent drilling / well-engineering personnel under the approved well program, company procedures, client requirements, and applicable regulations.");

  return lines.join("\n");
}
export const lotFitChecklist = (st) => {
  const out = [];
  const h = st.header || {}, setup = st.setup || {}, data = st.data || {};
  if (!h.reportDate) out.push("Report date missing");
  if (!h.rigName) out.push("Rig name missing");
  if (!h.wellName) out.push("Well name missing");
  if (!h.testType) out.push("Test type (LOT / FIT / XLOT) missing");
  if (!setup.shoeTvd) out.push("Casing shoe TVD missing");
  if (!setup.mudWeightPpg) out.push("Mud weight at test missing");
  if (!data.maxSurfPsi) out.push("Max surface pressure missing");
  if (!data.pumpRate) out.push("Pump rate missing");
  return out;
};
export const LOT_FIT_QUICK_PHRASES = [
  "Pumped at constant rate.",
  "Pressure recorded by chart and gauge.",
  "Volumes verified against pump strokes.",
  "Bleed-off observed and recorded.",
  "Witnessed by company representative.",
  "Interpretation pending well-engineering review.",
];
